import React, { useEffect, useState } from "react";
import "./Footer.css";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../utils/api";

const Footer = ({ userForDomain }) => {
 const year = new Date().getFullYear();

 const slug = userForDomain?.slug;

 const hostname = window.location.hostname;
 const mainDomain = "sportinghip.com";

 const isLocalhost = hostname === "localhost";

 const isCustomDomain =
  !isLocalhost &&
  hostname !== mainDomain &&
  hostname !== "www.sportinghip.com";

 const fallbackSlug = window.location.pathname.split("/")[1];

 const basePath = isCustomDomain
  ? ""
  : `/${slug || fallbackSlug || ""}`;

 const navigate = useNavigate();
 const location = useLocation();
  const isHomepage =
    location.pathname === (basePath || "/") ||
    (isCustomDomain && location.pathname === "/");
 const { checkAuth, logout } = useAuth();

 const [isAuthenticated, setIsAuthenticated] = useState(false);
 const [publicUser, setPublicUser] = useState(null);

 const [footerSettings, setFooterSettings] = useState(null);

 const capitalizeFirst = (str) =>
  str
   ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
   : "";

 // =========================
 // PUBLIC AUTH
 // =========================
 useEffect(() => {
  const loadPublicAuth = async () => {
   try {
    const { data } = await api.get("/api/auth/public", {
     withCredentials: true,
    });

    setIsAuthenticated(data?.authenticated || false);
    setPublicUser(data?.user || null);
   } catch (error) {
    setIsAuthenticated(false);
    setPublicUser(null);
   }
  };

  loadPublicAuth();
 }, []);

 // =========================
 // FOOTER SETTINGS
 // =========================
 useEffect(() => {
  const loadFooterSettings = async () => {
   try {
    const params = {};

    if (userForDomain?.id) {
     params.organizationId = userForDomain.id;
    } else if (userForDomain?.slug) {
     params.slug = userForDomain.slug;
    }

    if (!params.organizationId && !params.slug) {
     setFooterSettings(null);
     return;
    }

    const res = await api.get(
     "/api/organizations/public/nav-pages",
     {
      params,
     },
    );

    setFooterSettings(res.data?.settings || null);
   } catch (error) {
    console.error(
     "Footer settings fetch failed:",
     error,
    );

    setFooterSettings(null);
   }
  };

  loadFooterSettings();
 }, [userForDomain?.id, userForDomain?.slug]);

 // =========================
 // DYNAMIC FOOTER VALUES
 // =========================
  const footerBackgroundColor = isHomepage
    ? footerSettings?.footer_home_background_color || "#ffffff"
    : footerSettings?.footer_inner_background_color || "#ffffff";

  const footerTextColor = isHomepage
    ? footerSettings?.footer_home_text_color || "#555555"
    : footerSettings?.footer_inner_text_color || "#555555";

  const footerLinkColor = isHomepage
    ? footerSettings?.footer_home_link_color || "#555555"
    : footerSettings?.footer_inner_link_color || "#555555";

  const footerBorderColor = isHomepage
    ? footerSettings?.footer_home_border_color || "transparent"
    : footerSettings?.footer_inner_border_color || "transparent";

 const footerPaddingY =
  Number(footerSettings?.footer_padding_y) || 24;

 return (
  <footer
   className="footer_container"
   style={{
    "--footer-background-color":
     footerBackgroundColor,

    "--footer-text-color":
     footerTextColor,

    "--footer-link-color":
     footerLinkColor,

    "--footer-border-color":
     footerBorderColor,

    "--footer-padding-y":
     `${footerPaddingY}px`,
   }}
  >
   <div className="footer_main">
    {/* LEFT */}
    <div className="footer_left">
     <p>
      Site Powered by{" "}
      <a
       href="https://sportinghip.com"
       target="_blank"
       rel="noreferrer"
      >
       SportingHip.com
      </a>
     </p>
    </div>

    {/* CENTER */}
    <div className="footer_center">
     <p>
      © {year}{" "}
      {capitalizeFirst(
       userForDomain?.slug,
      ) ||
       userForDomain?.name ||
       "League"}
      .
      <br />
      All Rights Reserved.
     </p>
    </div>

    {/* RIGHT */}
    <div className="footer_right">
     {!isAuthenticated && (
      <span
       className="admin_link"
       onClick={() =>
        navigate(`${basePath}/login`, {
         state: {
          from:
           location.pathname +
           location.search,
         },
        })
       }
      >
       Admin Sign In
       <span className="footer_admin_arrow">
        ▶
       </span>
      </span>
     )}

     {isAuthenticated &&
      publicUser?.organization_id && (
       <span
        className="admin_link"
        onClick={async () => {
         await checkAuth();

         const canUseSaaS =
          publicUser?.role ===
          "super_admin" ||
          publicUser?.plan ===
          "saas_active";

         if (canUseSaaS) {
          navigate(
           "/dashboard/seasons",
          );
         } else {
          navigate(
           `/dashboard/organizations/${publicUser.organization_id}`,
          );
         }
        }}
       >
        Manage Site ⚙
       </span>
      )}

     {isAuthenticated && (
      <span
       className="admin_link"
       onClick={async () => {
        await logout();

        setIsAuthenticated(false);
        setPublicUser(null);

        navigate(0);
       }}
      >
       Logout
      </span>
     )}
    </div>
   </div>
  </footer>
 );
};

export default Footer;